<template>
  <div class="tw:space-y-6 tw:animate-fade-in">
    <div v-if="isIndex" class="tw:space-y-6">
      <div class="tw:flex tw:flex-col tw:gap-1.5 tw:pb-2 tw:border-b tw:border-slate-800/40">
        <h1 class="tw:text-2xl tw:font-extrabold tw:tracking-tight tw:text-slate-100">System Ops</h1>
        <p class="tw:text-sm tw:text-slate-400">Inspect delivery activity and operational diagnostics.</p>
      </div>

      <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4">
        <router-link
          v-for="section in sections"
          :key="section.name"
          :to="{ name: section.targetRouteName }"
          class="tw:relative tw:group tw:flex tw:items-start tw:gap-4 tw:p-4.5 tw:bg-slate-900/20 hover:tw:bg-indigo-500/[0.03] tw:border tw:border-slate-800/60 hover:tw:border-indigo-500/30 tw:rounded-2xl tw:transition-all tw:duration-300 hover:tw:shadow-[0_8px_30px_rgb(99,102,241,0.04)] tw:cursor-pointer"
        >
          <div class="tw:w-11 tw:h-11 tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:rounded-xl tw:border tw:transition-all tw:duration-300 group-hover:tw:scale-105 tw:bg-indigo-500/10 tw:text-indigo-400 tw:border-indigo-500/20">
            <font-awesome-icon :icon="section.icon" class="tw:text-lg" />
          </div>

          <div class="tw:flex tw:flex-col tw:gap-1 tw:mt-0.5">
            <span class="tw:text-sm tw:font-bold tw:text-slate-200 group-hover:tw:text-indigo-400 tw:transition-colors tw:duration-200">
              {{ section.label }}
            </span>
            <p class="tw:text-sm tw:text-slate-400 tw:leading-relaxed tw:font-medium">
              {{ section.description }}
            </p>
          </div>
        </router-link>
      </div>
    </div>

    <div v-else class="tw:space-y-6">
      <div class="tw:flex tw:flex-col tw:gap-1">
        <router-link
          :to="{ name: 'system-ops' }"
          class="tw:text-xs tw:font-bold tw:text-indigo-400 hover:tw:text-indigo-350 tw:transition-colors tw:flex tw:items-center tw:gap-1.5 tw:w-fit"
        >
          <font-awesome-icon icon="arrow-left" class="tw:text-2xs" />
          <span>System Ops</span>
        </router-link>
        <h1 class="tw:text-2xl tw:font-extrabold tw:tracking-tight tw:text-slate-100 tw:mt-1.5">{{ activeSectionLabel }}</h1>
      </div>

      <div class="tw:w-full">
        <router-view v-slot="{ Component }">
          <keep-alive :include="includeInKeepAlive">
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useRouterViewMeta } from '@/composables/useRouterViewMeta'

const route = useRoute()
const router = useRouter()
const { includeInKeepAlive } = useRouterViewMeta()

const isIndex = computed(() => route.name === 'system-ops')

const systemOpsRoute = computed(() => {
  return router.getRoutes().find(r => r.name === 'system-ops')
})

const sections = computed(() => {
  const children = systemOpsRoute.value?.children || []
  return children
    .filter(child => !child.redirect && child.name)
    .map(child => ({
      name: String(child.name),
      targetRouteName: String(child.name),
      label: String(child.meta?.label || child.name),
      description: String(child.meta?.description || ''),
      icon: child.meta?.icon || 'wrench'
    }))
})

const activeSectionLabel = computed(() => {
  const matched = route.matched
  const rootIndex = matched.findIndex(m => m.name === 'system-ops')
  if (rootIndex !== -1 && rootIndex + 1 < matched.length) {
    const sectionRoute = matched[rootIndex + 1]
    return String(sectionRoute.meta?.label || sectionRoute.name || 'System Ops')
  }

  return 'System Ops'
})
</script>
